import { useContext, useEffect, useRef, useState } from "react";
import AuthContext from "../context/AuthProvider";
import axios from "../api/axios";
import { useNavigate } from "react-router-dom";
import { AlertCircle } from "lucide-react";
import logo from '../assets/images/gentium.png';

const LOGIN_URL = '/api/login';

const Login = () => {
    const { setAuth } = useContext(AuthContext);
    const userRef = useRef();
    const errRef = useRef();
    const navigate = useNavigate();

    const [user, setUser] = useState("");
    const [pwd, setPwd] = useState("");
    const [errMsg, setErrMsg] = useState("");

    useEffect(() => {
        userRef.current.focus();
    }, []);

    useEffect(() => {
        setErrMsg("");
    }, [user, pwd]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const response = await axios.post(LOGIN_URL,
                JSON.stringify({ email: user, password: pwd }),
                {
                    headers: { 'Content-Type': 'application/json' },
                }
            );

            const accessToken = response?.data.token;
            window.localStorage.setItem('token', accessToken);
            const role = response?.data?.user.role;

            setAuth({ user, pwd, role, accessToken });
            setUser('');
            setPwd('');
            
            
            if (role.includes('admin')) {
                navigate('/dashboard');
            } else {
                navigate('/caisse');
            }
        } catch (err) {
            console.error("Error in Login Request:", err);

            if (!err.response) {
                setErrMsg('No response from server');
            } else if (err.response?.status === 400) {
                setErrMsg("Username or password missing");
            } else if (err.response?.status === 401) {
                setErrMsg('Invalid email or password');
            } else {
                setErrMsg('Login failed');
            }

            errRef.current?.focus();
        }
    };

    return (
        <div className="bg-gradient-to-r from-indigo-100 to-stone-300 min-h-screen flex items-center justify-center">
            <div className="max-w-md w-full">
                <form
                    onSubmit={handleSubmit}
                    className="bg-white shadow-lg py-8 px-6 rounded-lg space-y-6 border border-gray-200"
                >
                    <div className="flex justify-center mb-4">
                        <img src={logo} alt="Gentium Logo" className="h-20" />
                    </div>

                    <div className="text-center mb-2">
                        <h2 className="text-2xl font-extrabold text-[#610c63]">
                            Welcome to Shtatto Booking
                        </h2>
                        <p className="mt-1 text-sm text-gray-600">
                            Please log in to your account
                        </p>
                    </div>

                    {errMsg && (
                        <div
                            ref={errRef}
                            className="bg-red-50 border border-red-200 rounded-md p-4 animate-bounce"
                            role="alert"
                        >
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <AlertCircle className="h-5 w-5 text-red-400" />
                                </div>
                                <div className="ml-3">
                                    <p className="text-sm text-red-700">{errMsg}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    <div>
                        <label
                            htmlFor="email"
                            className="block text-sm font-medium text-gray-700"
                        >
                            Email Address
                        </label>
                        <div className="mt-1">
                            <input
                                id="email"
                                name="email"
                                type="email"
                                ref={userRef}
                                autoComplete="email"
                                required
                                value={user}
                                onChange={(e) => setUser(e.target.value)}
                                className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#610c63] focus:border-[#610c63] sm:text-sm"
                                placeholder="you@example.com"
                            />
                        </div>
                    </div>

                    <div>
                        <label
                            htmlFor="password"
                            className="block text-sm font-medium text-gray-700"
                        >
                            Password
                        </label>
                        <div className="mt-1">
                            <input
                                id="password"
                                name="password"
                                type="password"
                                required
                                value={pwd}
                                onChange={(e) => setPwd(e.target.value)}
                                className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#610c63] focus:border-[#610c63] sm:text-sm"
                                placeholder="••••••••"
                            />
                        </div>
                    </div>

                    <div>
                        <button
                            type="submit"
                            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#610c63] hover:bg-[#7b2a6f] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#610c63] transition-colors duration-200"
                        >
                            Log In
                        </button>
                    </div>

                    <div className="text-center mt-4 flex justify-end">
                        <p className="text-xs text-gray-600">by Gentium</p>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default Login;